import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	EventType,
	IChatEventTypingMessage,
	IChatVoiceMessage,
	IFileResource,
	MessageType,
	ShowTypingState,
	isMediaMessage,
	isTextMessage,
	isVoiceMessage,
} from '@enuan/chat__utils';
import {
	ISubmitContextState,
	ISubmitContextCallback,
} from '../../contexts/submit.context';
import { useSendMessage } from '../../hooks/core/messages.hooks';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { useLogError } from '../ErrorBoundary/useLogError';
import { useRecorder } from './audio/hooks';

/** Timeout (ms) before the error banner auto-clears. */
const ERROR_DISPLAY_MS = 5_000;
/** Timeout (ms) before a text composing event transitions to PAUSED. */
const TEXT_PAUSE_MS = 5_000;
/** Timeout (ms) before an attach/record composing event transitions to PAUSED. */
const MEDIA_PAUSE_MS = 1_000 * 60 * 5;

/**
 * Return value of {@link useSubmitFormState}.
 * Contains the full submit-context value plus recorder helpers and
 * derived flags consumed by the form UI.
 */
export type SubmitFormState = {
	/** Value to feed into `submitContext.Provider`. */
	contextValue: ISubmitContextState & ISubmitContextCallback;

	/** Audio recorder capabilities and controls. */
	recorder: {
		isSupported: boolean;
		isRecording: boolean;
		start: () => Promise<void>;
		stop: (fileName?: string) => Promise<IChatVoiceMessage<IFileResource>>;
	};

	/** Whether the textarea (text input) should be visible. */
	showTextarea: boolean;
	/** Whether the record-audio button should be temporarily disabled. */
	isRecordAudioTempDisabled: boolean;
	/** Whether the attach button should be disabled. */
	isAttachDisabled: boolean;
	/** Whether the submit button should be disabled. */
	isSubmitDisabled: boolean;

	/** Form submit handler (calls `e.preventDefault()`). */
	onFormSubmit: (e: React.SyntheticEvent) => void;
};

/**
 * Encapsulates all mutable state and side-effects for the message
 * submission form.
 *
 * Manages:
 * - message / action / error / composingState
 * - typing-indicator lifecycle (COMPOSING → PAUSED → CLEARED)
 * - audio recording integration via {@link useRecorder}
 * - submit logic with auto-trim for text messages
 *
 * @param isInputLocked - whether the chat input is currently locked
 *   (e.g. a required form must be completed first).
 */
export function useSubmitFormState(isInputLocked: boolean): SubmitFormState {
	const logError = useLogError('SubmitForm');
	const sendMessage = useSendMessage();

	// ── core state ──────────────────────────────────────────────────
	const [message, setMessage] = useState<ISubmitContextState['message']>();
	const [action, setAction] = useState<ISubmitContextState['action']>();
	const [error, setError] = useState<ISubmitContextState['error']>();
	const [composingState, setComposingState] = useState<ShowTypingState>();

	// ── reset action when message is cleared ────────────────────────
	useDidUpdateEffect(() => {
		if (!message) {
			setAction(undefined);
		}
	}, [message]);

	// ── auto-clear error after ERROR_DISPLAY_MS ─────────────────────
	useEffect(() => {
		if (error) {
			setMessage(undefined);
			const timeout = window.setTimeout(
				() => setError(undefined),
				ERROR_DISPLAY_MS,
			);
			return () => window.clearTimeout(timeout);
		}
	}, [error]);

	// ── typing indicator: send composing events ─────────────────────
	const sendComposingState = useCallback(
		(state: ShowTypingState) => {
			const event: Pick<IChatEventTypingMessage, '_data' | '_type'> = {
				_type: MessageType.EVENT,
				_data: { name: EventType.TYPING, parameters: { state } },
			};
			sendMessage(event, true).catch((err) =>
				logError('typing', { error: err, payload: { event } }),
			);
		},
		[sendMessage, logError],
	);

	useDidUpdateEffect(() => {
		if (composingState != null) {
			sendComposingState(composingState);
		}
	}, [composingState]);

	// ── audio recorder ──────────────────────────────────────────────
	const {
		isSupported: isRecordingAudioSupported,
		isRecording,
		start: startRecording,
		stop: stopRecording,
	} = useRecorder(setError);

	const isRecorded = isVoiceMessage(message);

	useDidUpdateEffect(() => {
		if (isRecording || isRecorded) {
			setAction('recording');
		}
	}, [isRecorded, isRecording, setAction]);

	// ── composing-state lifecycle (pause timeout) ───────────────────
	const pauseTimeout = useRef<number>(undefined);

	useDidUpdateEffect(() => {
		window.clearTimeout(pauseTimeout.current);

		if (message || action) {
			if (!isMediaMessage(message) && !isVoiceMessage(message)) {
				setComposingState(ShowTypingState.COMPOSING);
			}
			if (message) {
				pauseTimeout.current = window.setTimeout(() => {
					setComposingState(ShowTypingState.PAUSED);
				}, TEXT_PAUSE_MS);
			} else if (action === 'attaching' || isRecorded) {
				pauseTimeout.current = window.setTimeout(() => {
					setComposingState(ShowTypingState.PAUSED);
				}, MEDIA_PAUSE_MS);
			}
		} else {
			setComposingState(ShowTypingState.CLEARED);
		}
	}, [message, action, isRecorded]);

	// ── submit ──────────────────────────────────────────────────────
	const submitForm = useCallback(
		(_message?: ISubmitContextState['message']): Promise<string> => {
			if (isInputLocked) {
				throw new Error('Submit form locked');
			}
			const msg = _message ?? message;
			if (action === 'recording' && isRecording && !msg) {
				setAction(undefined);
				return stopRecording().then(submitForm);
			}
			if (msg) {
				setMessage(undefined);
				window.clearTimeout(pauseTimeout.current);
				setComposingState(undefined);
				if (isTextMessage(msg)) {
					return sendMessage({ ...msg, _data: msg._data.trim() });
				}
				return sendMessage(msg);
			}
			throw new Error('No message to submit');
		},
		[isInputLocked, message, action, isRecording, stopRecording, sendMessage],
	);

	const onFormSubmit = useCallback(
		(e: React.SyntheticEvent) => {
			e.preventDefault();
			submitForm().catch((err) => logError('onFormSubmit', { error: err }));
		},
		[submitForm, logError],
	);

	// ── derived flags ───────────────────────────────────────────────
	const showTextarea =
		(!message || isTextMessage(message)) && action !== 'recording';

	const isRecordAudioTempDisabled =
		!!message || (!!action && action !== 'recording');
	const isAttachDisabled = !!message && !isMediaMessage(message);
	const isSubmitDisabled = !message && action !== 'recording';

	// ── memoised objects (prevent unnecessary re-renders) ───────────
	const contextValue = useMemo<SubmitFormState['contextValue']>(
		() => ({
			message,
			setMessage,
			error,
			setError,
			action,
			setAction,
			submitForm,
			composingState,
			setComposingState,
		}),
		[message, error, action, submitForm, composingState],
	);

	const recorder = useMemo<SubmitFormState['recorder']>(
		() => ({
			isSupported: isRecordingAudioSupported,
			isRecording,
			start: startRecording,
			stop: stopRecording as SubmitFormState['recorder']['stop'],
		}),
		[isRecordingAudioSupported, isRecording, startRecording, stopRecording],
	);

	return {
		contextValue,
		recorder,
		showTextarea,
		isRecordAudioTempDisabled,
		isAttachDisabled,
		isSubmitDisabled,
		onFormSubmit,
	};
}
